import type { SensorSubType } from '../components/SensorTypeSelector'

/**
 * Compare-mode sensor selection tokens.
 *
 * A selection entry in `CompareSelection.sensorPorts` is normally a bare port
 * ("A1", "C2") or one of the virtual device-level ports ("T0", "BATT", "PWR").
 *
 * Sub-typed sensors (rainfall / flow meter) expose three metrics from a single
 * physical port, so mixed mode needs to say *which* metric is selected. Those
 * selections use a composite token `PORT~METRIC`, e.g. "A2~rate".
 *
 * The metric codes are deliberately generic (vol/rate/tot) rather than the full
 * `SensorSubType` keys: the product configured at the port is what decides
 * whether "rate" means `rainfallRate` or `flowRate`, so encoding the type in the
 * token would just create a second source of truth that can contradict the
 * device config. Use `metricToSubType()` to resolve one at consumption time.
 *
 * `~` is URL-unreserved and is not used by the compare URL format, which already
 * spends `,` (between devices), `:` (device/ports) and `.` (between ports).
 */

export type SubMetric = 'vol' | 'rate' | 'tot'

export const SUB_METRICS: readonly SubMetric[] = ['vol', 'rate', 'tot']

/** Compact labels for the grid's stacked mini-checkboxes. */
export const SUB_METRIC_LABELS: Record<SubMetric, string> = {
  vol: 'Vol',
  rate: 'Rate',
  tot: 'Tot',
}

/** Full labels for the bulk-toggle pills. */
export const SUB_METRIC_PILL_LABELS: Record<SubMetric, string> = {
  vol: 'Volume',
  rate: 'Rate',
  tot: 'Total',
}

const TOKEN_DELIMITER = '~'

// Physical ports plus the virtual device-level ports (T0 = board temperature,
// BATT/PWR = the battery and Port C power maps from /battery-readings). Kept in
// sync with SensorTypeSelector's constants.
const VALID_PORT_TOKEN = /^(A[12]|B[12]|C[123]|T0|BATT|PWR)(~(vol|rate|tot))?$/

/** Build a selection token. A null/undefined metric yields the bare port. */
export function makePortToken(port: string, metric?: SubMetric | null): string {
  return metric ? `${port}${TOKEN_DELIMITER}${metric}` : port
}

/**
 * Split a selection token into its physical port and (optional) sub-metric.
 * Bare tokens — including every token written before mixed-mode sub-metrics
 * existed — parse as `{ port, metric: null }`.
 */
export function parsePortToken(token: string): { port: string; metric: SubMetric | null } {
  const idx = token.indexOf(TOKEN_DELIMITER)
  if (idx === -1) return { port: token, metric: null }
  const port = token.slice(0, idx)
  const metric = token.slice(idx + 1)
  return SUB_METRICS.includes(metric as SubMetric)
    ? { port, metric: metric as SubMetric }
    : { port, metric: null }
}

export function isValidPortToken(token: string): boolean {
  return VALID_PORT_TOKEN.test(token)
}

/** Resolve a generic metric code against the sensor type actually at the port. */
export function metricToSubType(
  subTypeKey: 'rainfall' | 'flowMeter',
  metric: SubMetric
): SensorSubType {
  if (subTypeKey === 'rainfall') {
    switch (metric) {
      case 'vol': return 'rainfallVolume'
      case 'rate': return 'rainfallRate'
      case 'tot': return 'rainfallTotal'
    }
  }
  switch (metric) {
    case 'vol': return 'flowVolume'
    case 'rate': return 'flowRate'
    case 'tot': return 'flowTotal'
  }
}
